<!-- One moment on the clock, and every ticket that starts at it.

     Today used to be grouped by STRATEGY: a sheet per strategy, its tiers
     underneath, and no way at all to see what was about to start. That is the
     wrong outer axis for a screen somebody opens twenty minutes before a game.
     The day is a timeline now, so the sheet is a KICK-OFF — "7:20 pm",
     "Tomorrow 9:21 am" — and the strategy is an eyebrow on the ticket, where
     it belongs: a note about whose opinion this is, under a heading about
     when it runs.

     EXPIRED TICKETS STAY. A ticket whose first leg has started cannot be
     placed, but it is still what the app proposed, and a screen that deleted
     it would be a screen with no memory. It prints dimmed, above the Now rule,
     with its legs reading "started" and every stamp it earned still on it.

     The sheet is the one in app.css — the same paper a strategy section used
     to be printed on, for the same reason: without it eight tickets stacked on
     one ground have no join to see. -->
<template>
  <section class="sheet kickoff-group" :class="{ 'kickoff-group--past': past, 'kickoff-group--feed': feed }">
    <h2 class="section-title kickoff-title">
      <!-- WHAT IS IN THIS CLUSTER, as shapes, beside the time it starts. The
           union of every ticket under the heading — three races and a football
           multi at 2:40 shows a flag and a ball — so a reader scrolling the
           roll can skip a whole sheet without reading a word of it.

           `aria-hidden` and no sr-only line: the heading is a TIME, the tickets
           underneath each say their own sports in their own label, and a
           heading that recited the day's codes before every cluster would be
           the one thing a screen reader could not skip. -->
      <span class="kickoff-when">
        {{ label }}
        <span v-if="glyphs.length" class="kickoff-glyphs" aria-hidden="true">
          <IonIcon v-for="g in glyphs" :key="g.sport" class="sport-glyph" :icon="g.icon" />
          <span v-if="glyphsOver" class="glyph-more print">+{{ glyphsOver }}</span>
        </span>
      </span>
      <span class="kickoff-count">{{ countLabel }}</span>
    </h2>

    <div v-for="entry in entries" :key="entry.id" class="kickoff-entry" :class="{ 'kickoff-entry--expired': entry.state === 'expired' }">
      <Ticket
        :multi="entry.ticket.multi"
        :legs="entry.ticket.legs"
        :live="entry.ticket.live ?? null"
        :universe="universe"
        :now="now"
        :eyebrow="eyebrowFor(entry)"
        :badge="badgeFor(entry)"
        :stamp="stampFor(entry)"
        :stamp-tone="toneFor(entry)"
        :dealt-at="entry.dealtAt ?? ''"
        :density="density"
        compact
        interactive
        @open="(id) => emit('open', id)"
      />

      <!-- The line under the ticket: where its numbers came from, and the way
           into the strategy's own words.

           The stamp only prints on a ticket that was DEALT later than its
           prices were read — a refill — because that is the one case where
           "dealt at 2:10" and "priced at 9:11" are different facts and a
           punter has to know which is which. Nothing fetched anything to say
           it.

           The ⓘ used to sit in a strategy's section header, and there are no
           strategy sections any more. It belongs to the ticket now, which is
           where the strategy's name is; outside the ticket, because the
           ticket's root is a <button>. Ionic strips `aria-label` off the host,
           so the name is carried by the screen-reader-only span. -->
      <!-- The ⓘ sits in the ticket's top corner, beside the strategy's name it
           explains — outside the ticket's <button> in the DOM, on top of it on
           the page. It used to share a row under the legs with the provenance
           line, and on the many tickets that have no provenance line that row
           was a blank line with a glyph at the end of it. -->
      <IonButton
        class="info-btn"
        fill="clear"
        size="small"
        :aria-label="explainLabel(entry)"
        @click="emit('explain', entry.strategy)"
      >
        <IonIcon slot="icon-only" :icon="informationCircleOutline" aria-hidden="true" />
        <span class="sr-only">{{ explainLabel(entry) }}</span>
      </IonButton>
      <p v-if="metaFor(entry)" class="small-print kickoff-meta">{{ metaFor(entry) }}</p>

      <!-- Outside the ticket, never inside it: the ticket's root is a <button>
           when it is interactive. -->
      <!-- An expired ticket has a leg under way, and a bookmaker will not take
           that without a phone call — which is the case the "Only matches yet
           to start" setting exists for. So the action goes, and the line says
           why. `ticketState` already keeps a ticket live to its last kick-off
           when the setting is off, so "expired" here always means "too late".

           AND IT MEANS IT NOW, rather than fifteen minutes early: the buffer is
           gone (`LIVE_BUFFER_MS`), so this line appears when the first leg
           actually starts and the ticket above it prints that leg struck
           through. The two say the same thing, once in a sentence and once on
           the leg it is about. -->
      <p v-if="entry.state === 'expired'" class="small-print too-late">Started · too late to place</p>
      <TicketActions
        v-else
        :multi="entry.ticket.multi"
        :density="density"
        compact
        @place="emit('place', entry.ticket)"
        @copy="emit('copy', entry.ticket)"
        @history="emit('history', entry.ticket)"
      />
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import { IonButton, IonIcon } from '@ionic/vue';
import { informationCircleOutline } from 'ionicons/icons';
import { RESULT_LABEL } from '../lib/history-view.js';
import Ticket from './Ticket.vue';
import TicketActions from './TicketActions.vue';
import { signedPct, time } from '../lib/format.js';
import { glyphsFor, legsOfAll, moreGlyphs } from '../lib/sport-glyphs.js';
import { CLASSIC } from '../lib/engine/strategies/classic.js';
// The two words a strategy that has not finished proving itself carries.
import { ON_TRIAL } from '../lib/engine/proof.js';

const props = defineProps({
  /** The cluster's heading — `kickoffLabel` from `lib/timeline.js`. */
  label: { type: String, required: true },
  /** Timeline entries sharing this kick-off: `{ id, ticket, strategy, state, … }`. */
  entries: { type: Array, default: () => [] },
  /** Epoch ms. Handed straight to each ticket; pass the store's ticking clock. */
  now: { type: Number, default: () => Date.now() },
  /** This whole cluster is behind the fold: print it quieter. */
  past: { type: Boolean, default: false },
  /**
   * Today's board, handed straight to each ticket so the feed can print the
   * margin line — what the same legs would come to one by one at the best price
   * going. The cluster has no opinion about it; it is only in the way.
   *
   * The drift arrows beside a leg need nothing here: the feed already attaches
   * `live` to the entries it re-prices (`TodayPage.priced`), and this component
   * simply stopped passing it on — which is why the arrows only ever showed on
   * the ticket screen.
   */
  universe: { type: Object, default: null },
  /**
   * How much paper a ticket in this cluster is allowed — handed straight to
   * `Ticket` and `TicketActions`. `'feed'` is Today's continuous roll; every
   * other caller leaves it alone.
   */
  density: { type: String, default: 'default' },
  /**
   * Where each strategy stands against the bar: `{ [strategyId]: state }` as
   * `engine/proof.js` names them. Only `'on-trial'` changes anything here — it
   * is admitted on the eyebrow — and a missing map leaves every ticket exactly
   * as it was.
   */
  proof: { type: Object, default: null },
});

const feed = computed(() => props.density === 'feed');

const emit = defineEmits(['open', 'place', 'copy', 'history', 'explain']);

// How far down the ranking is worth a number on the paper — the same three
// StrategySection used, so a badge means what it has always meant.
const TOP = 3;

const countLabel = computed(() => {
  const n = props.entries.length;
  return `${n} ticket${n === 1 ? '' : 's'}`;
});

// Every sport under this heading, as shapes — the same cap and the same order
// one ticket's eyebrow uses, over every leg of every ticket in the cluster.
const clusterLegs = computed(() => legsOfAll(props.entries));
const glyphs = computed(() => glyphsFor(clusterLegs.value));
const glyphsOver = computed(() => moreGlyphs(clusterLegs.value));

/** Whose ticket it is. A strategy deleted since is named off the ticket itself. */
function strategyName(entry) {
  return entry.strategy?.name ?? entry.ticket?.multi?.label ?? 'Classic Four Ways';
}

/**
 * THE EYEBROW, AND WHAT IT HAS TO ADMIT. A strategy whose model has cleared the
 * harness but not the forward half of the bar still prints — it has passed the
 * only test that exists before a leg does — and it says so on every ticket it
 * prints: "Elo Edge · on trial". See `engine/proof.js` for what the two halves
 * are and why the second one is the one that costs something to be wrong about.
 *
 * `proof` is a plain map of strategy id to state, computed once per pad by the
 * screen above. Absent, or absent for this strategy, prints the name alone,
 * which is every ticket the app has ever printed.
 */
function eyebrowFor(entry) {
  const name = strategyName(entry);
  const id = entry.strategy?.id ?? entry.ticket?.multi?.strategyId;
  return props.proof?.[id] === 'on-trial' ? `${name} · ${ON_TRIAL}` : name;
}

const explainLabel = (entry) => `How ${strategyName(entry)} works`;

// A settled bet reads its result, not "Placed": the feed is the record of the
// day, and a voided ticket that still said PLACED was the record lying. Empty
// hands the ticket back to its own stamps (Placed, Stale). Same ink map as
// ProposedDay — LOST and VOID keep the plain red.
const TONE = { won: 'won', pending: 'pending', unresulted: 'unresulted' };
const stampFor = (entry) => (entry.result ? RESULT_LABEL[entry.result] ?? '' : '');
const toneFor = (entry) => (entry.result ? TONE[entry.result] ?? '' : '');

/**
 * What the ranking made of this ticket. Never on Classic: it has no model and
 * measures no edge, so a value figure there would be the pad inventing an
 * opinion it does not hold.
 */
function badgeFor(entry) {
  const multi = entry.ticket?.multi;
  // WHERE TO PLACE IT, on the head line beside the tier: "Low risk · at Neds".
  // Only a ticket that names ONE book carries `bookmakerKey`: a single, or a
  // `best-book` multi whose legs were all re-priced at the one counter. A
  // ticket whose legs sit at three shops cannot be placed anywhere in
  // particular, and "at Best price" is not an instruction. It is the head line
  // and not a note under the legs because it is the first thing a punter
  // needs: which app to open.
  const book = multi?.bookmakerKey ? `at ${multi.bookmaker}` : '';
  if ((multi?.strategyId ?? CLASSIC.id) === CLASSIC.id) return book;
  const pick = entry.ticket?.pick;
  const value = pick && Number.isFinite(pick.valueVsMarket)
    ? (Number.isFinite(pick.rank) && pick.rank <= TOP ? `#${pick.rank} value` : `${signedPct(pick.valueVsMarket)} vs market`)
    : '';
  return [value, book].filter(Boolean).join(' · ');
}

/** "dealt 2:10 pm · prices from 9:11 am", or '' when the two are the same moment. */
function metaFor(entry) {
  const dealt = Date.parse(entry.dealtAt ?? '');
  const priced = Date.parse(entry.pricesFrom ?? '');
  if (!Number.isFinite(dealt) || !Number.isFinite(priced) || dealt <= priced) return '';
  return `dealt ${time(entry.dealtAt)} · prices from ${time(entry.pricesFrom)}`;
}
</script>

<style scoped>
/* The gap is on both edges and it is still ONE gap: adjacent margins collapse,
   so 32 above and 32 below reads as 32 between. Written twice rather than once
   because the Now rule prints BETWEEN two of these and belongs on the desk. */
.kickoff-group {
  margin: var(--sp-6) 0;
}

/* The first sheet's top margin is bare desk under the dateline, on the one
   screen that should open on tickets. */
.kickoff-group:first-of-type {
  margin-top: var(--sp-3);
}

/* The last thing on the sheet stops where the sheet's padding does. */
.kickoff-group > :last-child {
  margin-bottom: 0;
}

/* The heading is the first thing ON the sheet, so the sheet's own padding is
   the air above it — the shared rule's 24px top margin would push the time off
   its own paper. Same reasoning as the board sheet's title on Matches. */
.kickoff-title {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--sp-3);
  min-height: var(--tap);
  margin-top: 0;
}

/* The time and its shapes are one thing, so they share one flex child and the
   count keeps the far end of the line to itself. */
.kickoff-when {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  min-width: 0;
}

/* Beside the time, at the ink the count is set in: the heading is the word, and
   these are a note about what is under it. 14px is the same size the ticket
   eyebrow's shapes take, so one kind of mark is one size everywhere. */
.kickoff-glyphs {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.kickoff-glyphs .sport-glyph {
  width: 14px;
  height: 14px;
  color: var(--carbon-faint);
}

.kickoff-glyphs .glyph-more {
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0;
  color: var(--carbon-faint);
}

/* How many tickets start at this time, at the end of the same line. Same size,
   lighter ink and no tracking: a note about the heading, not part of it. */
.kickoff-count {
  font-weight: 400;
  letter-spacing: normal;
  text-transform: none;
  color: var(--carbon-faint);
  white-space: nowrap;
}

/* PAST IS QUIETER, NOT GONE. Opacity rather than a second set of inks: every
   ink on the ticket — the tier colour, the stamps, the strikethrough on a
   started leg — stays exactly the ink it is, and the whole thing simply
   recedes. A ticket that has been and gone is still readable, and its Placed
   stamp is often the reason somebody scrolled up to it. */
.kickoff-entry {
  position: relative;
}

.kickoff-entry--expired {
  opacity: 0.55;
}

/* The provenance line and the ⓘ share one row under the ticket they are about,
   pulled up into the ticket's own bottom margin so the pair reads as part of
   the ticket rather than as something adrift between two. The button carries
   its own 44px box, so the row is as short as it is allowed to get. */
.kickoff-meta {
  margin: calc(-1 * var(--sp-3)) 0 0;
}

/* A 44px target in BOTH directions that takes up no more room than its glyph.
   `height` alone was only half the job on the section header this came from:
   the width of an icon button is its glyph plus its padding, which came to
   34px. `ionic.css` publishes a `min-width` for this but scopes it to buttons
   in a toolbar, and this one lives in the content. WCAG 2.2 SC 2.5.8. */
.info-btn {
  position: absolute;
  top: 0;
  right: calc(-1 * var(--sp-2));
  z-index: 1;
  margin: 0;
  --padding-start: var(--sp-2);
  --padding-end: var(--sp-2);
  --color: var(--carbon-faint);
  height: var(--tap);
  min-width: var(--tap);
}
.too-late {
  margin: var(--sp-2) 0 0;
}

/* --- feed density ----------------------------------------------------------- */
/* The sheet a cluster is printed on, at the scale of a feed. `.sheet` is
   16/24 of padding because a strategy section used to be a page's worth of
   reading; a kick-off cluster in a roll is two or three tickets, and the
   sheet's job there is only to say where one cluster ends. */
.kickoff-group--feed {
  margin: var(--sp-4) 0;
  padding: var(--sp-3);
}

.kickoff-group--feed:first-of-type {
  margin-top: var(--sp-3);
}

/* ONE LINE, AND NO DEAD SPACE UNDER IT. The heading carried `min-height: 44px`
   — a tap target's worth of paper for something nobody taps — which printed as
   a band of blank sheet between the time and the first tear line. */
.kickoff-group--feed .kickoff-title {
  min-height: 0;
  margin: 0 0 var(--sp-2);
}

/* The provenance line and the ⓘ, at meta size, --sp-2 under the ticket they
   are about. The negative pull upward belongs to the default density, where
   the ticket beneath carries a --sp-4 margin to reach into; at this density
   there is nothing left to reach into and the pull would overlap the legs. */
.kickoff-group--feed .kickoff-meta {
  margin: var(--sp-2) 0 0;
}

.kickoff-group--feed .too-late {
  margin: var(--sp-1) 0 0;
}
</style>
